import { SelectOption } from 'global';
import {
  OpportuinitiesBaseColumns,
  OpportuinitiesDealerColumns,
  OpportuinitiesPartnerColumns,
  OpportunitiesListTabs,
  OpportunitiesTableColumn,
  TabInitialConfig,
} from './types';
import { DepartmentsCell } from 'pages/VendorOpportunity/mainVOListCells/DepartmentsCell';
import { DealTypeCell } from 'modules/proposals/ui/tables/cells';
import { TierCell } from './ui/components/opportunitiesList/tableCells/TierCell';
import { PartnerLogoCell } from './ui/components/opportunitiesList/tableCells/PartnerLogoCell';
import { DisplayStatusCell } from './ui/components/opportunitiesList/tableCells/DisplayStatusCell';
import { VOFilterType } from '../filters';

export const standardTabsInitialConfig: TabInitialConfig[] = [
  {
    label: 'My Opportunities',
    value: OpportunitiesListTabs.MyOpportunities,
  },
  {
    label: 'All Opportunities',
    value: OpportunitiesListTabs.AllOpportunities,
  },
  {
    label: 'Unassigned',
    value: OpportunitiesListTabs.Unassigned,
  },
];

export const dealPipelineTabInitialConfig: TabInitialConfig = {
  label: 'Deal Pipeline',
  value: OpportunitiesListTabs.DealPipeline,
};

export const displayStatusOptions: SelectOption<number>[] = [
  {
    label: 'Active',
    value: 100,
  },
  {
    label: 'Archive, Duplicate',
    value: 201,
  },
  {
    label: 'Archive, Testing',
    value: 200,
  },
  {
    label: 'Archive, Other',
    value: 202,
  },
];

export const baseColumnsConfig: Record<OpportuinitiesBaseColumns, OpportunitiesTableColumn> = {
  [OpportuinitiesBaseColumns.CREATED_ON]: {
    header: 'Created On',
    renderKey: 'createdOn',
    sortKey: 'createdOn',
  },
  [OpportuinitiesBaseColumns.BUSINESS_PROCESS_FLOWS]: {
    header: 'Business Process Flows',
    renderKey: 'businessProcessFlowsView',
    renderCell: ({ businessProcessFlowsView }) => <DepartmentsCell departmentsWithStages={businessProcessFlowsView} />,
  },
  [OpportuinitiesBaseColumns.CUSTOMER]: {
    header: 'Customer',
    renderKey: 'customerName',
    //sortKey: 'customerName',
  },
  [OpportuinitiesBaseColumns.STAGE]: {
    header: 'Stage',
    renderKey: 'applicationStage',
    sortKey: 'applicationStage',
  },
  [OpportuinitiesBaseColumns.STATUS]: {
    header: 'Status',
    renderKey: 'transactionStage',
    sortKey: 'transactionStage',
  },
  [OpportuinitiesBaseColumns.AMOUNT]: {
    header: 'Amount',
    renderKey: 'invoiceTotal',
    sortKey: 'invoiceTotal',
  },
  [OpportuinitiesBaseColumns.SALES_REP]: {
    header: 'Sales Rep',
    renderKey: 'salesRep',
    //sortKey: 'salesRep',
  },
  [OpportuinitiesBaseColumns.SALES_MANAGER]: {
    header: 'Sales Manager',
    renderKey: 'salesManager',
    //sortKey: 'salesManager',
  },
  [OpportuinitiesBaseColumns.FINANCE_MANAGER]: {
    header: 'Finance Manager',
    renderKey: 'financeManager',
    //sortKey: 'financeManager',
  },
  [OpportuinitiesBaseColumns.EQUIPMENT_DETAILS]: {
    header: 'Equipment Details',
    renderKey: 'equipmentDescription',
    isHidden: true,
    sortKey: 'equipmentDescription',
  },
  [OpportuinitiesBaseColumns.INVOICE_STATUS]: {
    header: 'Invoice Status',
    renderKey: 'invoiceStatus',
    isHidden: true,
    sortKey: 'invoiceStatus',
  },
  [OpportuinitiesBaseColumns.DEAL_TYPE]: {
    header: 'Deal Type',
    renderKey: 'inboundDealType',
    renderCell: ({ inboundDealType }) => <DealTypeCell type={inboundDealType ?? null} />,
    isHidden: true,
    //sortKey: 'inboundDealType',
  },
};

export const dealerColumnsConfig: Record<OpportuinitiesDealerColumns, OpportunitiesTableColumn> = {
  [OpportuinitiesDealerColumns.TIER]: {
    header: 'Tier',
    renderKey: 'creditTiersResult',
    renderCell: ({ creditTiersResult }) => <TierCell creditTiersResult={creditTiersResult} />,
    //sortKey: 'creditTiersResult',
  },
  [OpportuinitiesDealerColumns.SOURCE]: {
    header: 'Source',
    renderKey: 'partnerLogo',
    renderCell: ({ partnerLogo }) => <PartnerLogoCell partnerLogo={partnerLogo} />,
    isHidden: true,
  },
  [OpportuinitiesDealerColumns.CONTACT]: {
    header: 'Contact',
    renderKey: 'contactName',
    sortKey: 'contactName',
  },
  [OpportuinitiesDealerColumns.MAIN_SIGNOR]: {
    header: 'Main Signor',
    renderKey: 'mainSignor',
    isHidden: true,
    //sortKey: 'mainSignor',
  },
  [OpportuinitiesDealerColumns.OPPORTUNITY_TYPE]: {
    header: 'Opportunity Type',
    renderKey: 'entityType',
    sortKey: 'entityType',
  },
  [OpportuinitiesDealerColumns.LOCATION]: {
    header: 'Location',
    renderKey: 'location',
    sortKey: 'location',
  },
  [OpportuinitiesDealerColumns.STATUS_DETAIL]: {
    header: 'Status Detail',
    renderKey: 'statusDetail',
    isHidden: true,
    sortKey: 'statusDetail',
  },
  [OpportuinitiesDealerColumns.APPLICANT_TYPE]: {
    header: 'Applicant Type',
    renderKey: 'applicationType',
    isHidden: true,
    sortKey: 'applicationType',
  },
  [OpportuinitiesDealerColumns.APPLYING_FOR]: {
    header: 'Applying For',
    renderKey: 'applyingFor',
    sortKey: 'applyingFor',
  },
  [OpportuinitiesDealerColumns.DISPLAY]: {
    header: 'Display',
    renderKey: 'displayStatus',
    renderCell: ({ id, displayStatus }) => <DisplayStatusCell voId={id} displayStatus={displayStatus} />,
    sortKey: 'displayStatus',
  },
};

export const partnerColumnsConfig: Record<OpportuinitiesPartnerColumns, OpportunitiesTableColumn> = {
  [OpportuinitiesPartnerColumns.DEALER_NAME]: {
    header: 'Dealer Name',
    renderKey: 'dealerName',
    sortKey: 'dealerName',
  },
  [OpportuinitiesPartnerColumns.PARTNER_REP]: {
    header: 'Partner Rep',
    renderKey: 'partnerRepName',
    sortKey: 'partnerRepName',
  },
};

export const defaultTabValueBase = OpportunitiesListTabs.AllOpportunities;

export const defaultTabValueSalesRep = OpportunitiesListTabs.MyOpportunities;

export const setOfFiltersBase: VOFilterType[] = [
  VOFilterType.SET_OF_DATA,
  VOFilterType.CREATE_DATE_RANGE,
  VOFilterType.TIERS,
  VOFilterType.DISPLAY,
  VOFilterType.PAYMENT_TYPE,
  VOFilterType.STAGE,
  VOFilterType.STATUS,
  VOFilterType.LOCATION,
  VOFilterType.SALES_REP,
  VOFilterType.SALES_MANAGER,
  VOFilterType.FINANCE_MANAGER,
  VOFilterType.APPLYING_FOR,
  VOFilterType.OPPORTUNITY_TYPE,
  VOFilterType.BPF,
];

export const setOfFiltersSalesRep: VOFilterType[] = [
  VOFilterType.SET_OF_DATA,
  VOFilterType.CREATE_DATE_RANGE,
  VOFilterType.TIERS,
  VOFilterType.DISPLAY,
  VOFilterType.PAYMENT_TYPE,
  VOFilterType.STAGE,
  VOFilterType.STATUS,
  VOFilterType.LOCATION,
  VOFilterType.SALES_MANAGER,
  VOFilterType.FINANCE_MANAGER,
  VOFilterType.APPLYING_FOR,
  VOFilterType.OPPORTUNITY_TYPE,
  VOFilterType.BPF,
];

export const setOfFiltersPartner: VOFilterType[] = [
  VOFilterType.SET_OF_DATA,
  VOFilterType.CREATE_DATE_RANGE,
  VOFilterType.STAGE,
];
